import { z } from "zod";

export const FULLSCRIPT_SYSTEM = "fullscript";

// LEGACY plaintext token fields on the credentials `Basic` (ENG-5564). Nothing writes them any
// more: OAuth tokens live in the encrypted ProviderSecretStore. Only the store-miss fallback and
// the migration in lib/fullscript/token-store.ts read them, and the migration strips them.
export const FULLSCRIPT_IDENTIFIER_SYSTEM_ACCESS_TOKENS = `${FULLSCRIPT_SYSTEM}.access_tokens`;
export const FULLSCRIPT_EXTENSION_URL_ACCESS_TOKEN_EXPIRES_AT = `${FULLSCRIPT_SYSTEM}.access_token_expires_at`;
export const FULLSCRIPT_IDENTIFIER_SYSTEM_SECRET_TOKEN = `${FULLSCRIPT_SYSTEM}.secret_token`;
export const FULLSCRIPT_EXTENSION_URL_SECRET_TOKEN_EXPIRES_AT = `${FULLSCRIPT_SYSTEM}.secret_token_expires_at`;
export const FULLSCRIPT_EXTENSION_URL_REFRESH_TOKEN = `${FULLSCRIPT_SYSTEM}.refresh_tokens`;

//Patients and practitioners
export const FULLSCRIPT_IDENTIFIER_SYSTEM_PRACTITIONERS = `${FULLSCRIPT_SYSTEM}.practitioners`;
export const FULLSCRIPT_IDENTIFIER_SYSTEM_CLINICS = `${FULLSCRIPT_SYSTEM}.clinics`;
export const FULLSCRIPT_IDENTIFIER_SYSTEM_INTEGRATIONS = `${FULLSCRIPT_SYSTEM}.integrations`;

//Supplements plans
export const FULLSCRIPT_IDENTIFIER_SYSTEM_TREATMENT_PLANS = `${FULLSCRIPT_SYSTEM}.treatment_plans`;
export const FULLSCRIPT_IDENTIFIER_SYSTEM_VARIANTS = `${FULLSCRIPT_SYSTEM}.variants`;
export const FULLSCRIPT_IDENTIFIER_SYSTEM_SUPPLEMENTS = `${FULLSCRIPT_SYSTEM}.supplements`;

// Treatment-plan state we import on a sync. Fullscript's list endpoint has no
// server-side status filter, so we filter on this value client-side. Confirmed
// against the production API (states observed: "active", "draft").
export const FULLSCRIPT_TREATMENT_PLAN_STATE_ACTIVE = "active";

// A cancelled plan is closed upstream and can no longer be edited - Fullscript
// rejects PATCH/cancel on it with a 422. Confirmed against the production API.
export const FULLSCRIPT_TREATMENT_PLAN_STATE_CANCELLED = "cancelled";

/**
 * Interfaces for Fullscript webhook data structures
 */
export interface FullscriptDosage {
  recommended_amount: string | null;
  recommended_frequency: string | null;
  recommended_duration: string | null;
  format: string | null;
  additional_info: string | null;
  time_of_day: string[] | null;
}

export interface FullscriptRecommendation {
  variant_id: string;
  refill: boolean;
  units_to_purchase: number;
  take_with: string | null;
  dosage: FullscriptDosage;
  // Name isn't directly in the webhook data, but we'll extract it from dosage format
  name?: string;
}

export interface FullscriptResource {
  id: string;
  attachment_url: string;
  category: string;
  name: string;
  type: string;
}

export interface FullscriptLabRecommendation {
  id: string;
  name: string;
  requires_fasting: boolean;
  instructions: string | null;
}

/**
 * Treatment plan data structure from Fullscript webhook
 */
export interface FullscriptTreatmentPlan {
  id: string;
  state: string;
  patient: {
    id: string;
  };
  practitioner: {
    id: string;
  };
  available_at: string;
  /** Who created the plan: `api` for our API drafts, `fsjs` for the embed. Fullscript may omit it. */
  source?: string | null;
  /** The plan's note to the patient. One message for the whole plan, not one per product. */
  personal_message?: string | null;
  recommendations: FullscriptRecommendation[];
  resources: FullscriptResource[];
  lab_recommendations: FullscriptLabRecommendation[];
}

/**
 * Pagination metadata returned on Fullscript list/index endpoints.
 * https://fullscript.dev/technical-reference/pagination
 */
export interface FullscriptPaginationMeta {
  current_page: number;
  next_page: number | null;
  prev_page: number | null;
  total_pages: number;
  total_count: number;
}

/**
 * Fullscript webhook event structure
 */
export interface FullscriptWebhookEvent<T, EventType extends string = string> {
  event: {
    id: string;
    type: EventType;
    created_at: string;
    clinic_id: string;
    data: T;
    integration_id: string | null;
    vibrant?: FullscriptWebhookVibrantMetadata;
  };
}

/**
 * Metadata we attach to webhook events inside Vibrant.
 */
export interface FullscriptWebhookVibrantMetadata {
  source?: "webhook" | "client" | "draft-sweep";
  attempt?: number;
  providerId?: string;
  memberId?: string;
}

/**
 * Fullscript patient data structure
 */
export interface FullscriptPatient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  external_id: string;
}

/**
 * Fullscript product variant data structure
 */
export interface FullscriptProductVariant {
  id: string;
  sku: string;
  primary: boolean;
  units: string;
  unit_of_measure: string;
  availability: string;
  status: string;
  upc: string;
  msrp: string;
  supplier_sku: string;
  image_url_small: string;
  image_url_medium: string;
  image_url_large: string;
  product: {
    id: string;
    name: string;
    brand: {
      id: string;
      name: string;
      prefix: string;
    };
  };
  expected_return_date: string | null;
}

/**
 * Fullscript variant response structure
 */
export interface FullscriptVariantResponse {
  variant: FullscriptProductVariant;
}

/**
 * Type for the treatment plan created webhook event
 */
export interface TreatmentPlanCreatedEvent {
  event: FullscriptWebhookEvent<
    {
      treatment_plan: FullscriptTreatmentPlan;
    },
    "treatment_plan.created"
  >;
}

const FullscriptWebhookVibrantMetadataSchema = z
  .object({
    source: z.enum(["webhook", "client", "draft-sweep"]).optional(),
    attempt: z.number().int().nonnegative().optional(),
    providerId: z.string().optional(),
    memberId: z.string().optional(),
  })
  .optional();

/**
 * Real client-side event data structure from treatmentPlan.activated
 * The @fullscript/fullscript-js types are incomplete - this reflects actual runtime data
 */
export interface ClientTreatmentPlanActivatedEvent {
  id: string;
  type: "treatmentPlan.activated";
  data: {
    treatmentPlan: {
      id: string;
      state: string;
      patient: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
      };
      practitioner: {
        id: string;
      };
      availableAt: string;
      recommendations: Array<{
        variantId: string;
        refill: boolean;
        unitsToPurchase: number;
        dosage: {
          recommendedAmount: string | null;
          recommendedFrequency: string | null;
          recommendedDuration: string | null;
          format: string | null;
          additionalInfo: string | null;
        };
      }>;
      labRecommendations: FullscriptLabRecommendation[];
    };
  };
  createdAt: string;
  clinicId: string;
  oauth: {
    clientId: string;
  };
}

/**
 * Dosage as Fullscript reports it, normalized to one shape.
 *
 * The two Fullscript surfaces disagree on these key names, and nothing used to reconcile them:
 * the webhook body sends `recommended_amount` / `recommended_frequency` / `recommended_duration`,
 * while `GET /clinic/treatment_plans/:id` sends `amount` / `frequency` / `duration`. The REST
 * response was cast rather than parsed, so a plan read back over REST silently arrived with no
 * amount and no frequency, and every consumer fell through to "Use as directed".
 *
 * Accept both spellings and emit the `recommended_*` names, because that is what the write path
 * and `createInstructionsText` already read. The PATCH endpoint wants the bare names, and
 * `FullscriptClient.updateTreatmentPlan` maps back to them on the way out.
 */
export const FullscriptDosageSchema = z
  .object({
    recommended_amount: z.string().nullish(),
    recommended_frequency: z.string().nullish(),
    recommended_duration: z.string().nullish(),
    amount: z.string().nullish(),
    frequency: z.string().nullish(),
    duration: z.string().nullish(),
    format: z.string().nullish(),
    additional_info: z.string().nullish(),
    time_of_day: z.array(z.string()).nullish(),
  })
  .transform(
    (dosage): FullscriptDosage => ({
      recommended_amount: dosage.recommended_amount ?? dosage.amount ?? null,
      recommended_frequency: dosage.recommended_frequency ?? dosage.frequency ?? null,
      recommended_duration: dosage.recommended_duration ?? dosage.duration ?? null,
      format: dosage.format ?? null,
      additional_info: dosage.additional_info ?? null,
      time_of_day: dosage.time_of_day ?? null,
    }),
  );

export const FullscriptRecommendationSchema = z.object({
  variant_id: z.string(),
  refill: z
    .boolean()
    .nullish()
    .transform((value) => value ?? false),
  units_to_purchase: z
    .number()
    .nullish()
    .transform((value) => value ?? 1),
  take_with: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  dosage: FullscriptDosageSchema,
});

/**
 * The REST plan response, parsed rather than cast.
 *
 * Only the fields the write path reads are required. Everything else passes through, because a
 * vendor adding a field must not fail a live webhook or a reconcile.
 */
export const FullscriptTreatmentPlanSchema = z
  .object({
    id: z.string(),
    state: z.string(),
    patient: z.object({ id: z.string() }),
    practitioner: z.object({ id: z.string() }),
    available_at: z
      .string()
      .nullish()
      .transform((value) => value ?? ""),
    source: z.string().nullish(),
    personal_message: z.string().nullish(),
    recommendations: z.array(FullscriptRecommendationSchema).nullish(),
    resources: z.array(z.unknown()).nullish(),
    lab_recommendations: z.array(z.unknown()).nullish(),
  })
  .passthrough();

/**
 * Zod schema for validating the treatment plan created event
 */
export const TreatmentPlanCreatedSchema = z.object({
  event: z.object({
    event: z.object({
      id: z.string(),
      type: z.literal("treatment_plan.created"),
      created_at: z.string(),
      clinic_id: z.string(),
      data: z.object({
        treatment_plan: z.object({
          id: z.string(),
          state: z.string(),
          patient: z.object({
            id: z.string(),
          }),
          practitioner: z.object({
            id: z.string(),
          }),
          available_at: z.string(),
          recommendations: z.array(
            z.object({
              variant_id: z.string(),
              refill: z.boolean(),
              units_to_purchase: z.number(),
              take_with: z.string().nullable(),
              dosage: FullscriptDosageSchema,
            }),
          ),
          resources: z.array(
            z.object({
              id: z.string(),
              attachment_url: z.string(),
              category: z.string(),
              name: z.string(),
              type: z.string(),
            }),
          ),
          lab_recommendations: z.array(
            z.object({
              id: z.string(),
              name: z.string(),
              requires_fasting: z.boolean(),
              instructions: z.string().nullable(),
            }),
          ),
        }),
      }),
      integration_id: z.string().nullable(),
      vibrant: FullscriptWebhookVibrantMetadataSchema,
    }),
  }),
});

/**
 * The Fullscript event types that mean "this plan changed after it was created".
 *
 * `treatment_plan.updated` fires when the plan itself is edited.
 * `treatment_plan.recommendation.updated` fires when one product's dose or frequency changes,
 * and is by far the higher-volume of the two — a plan-level event does not always accompany it,
 * so both have to be handled or single-supplement edits are still missed.
 */
export const FULLSCRIPT_PLAN_UPDATE_EVENT_TYPES = [
  "treatment_plan.updated",
  "treatment_plan.recommendation.updated",
] as const;

export type FullscriptPlanUpdateEventType = (typeof FULLSCRIPT_PLAN_UPDATE_EVENT_TYPES)[number];

/**
 * Envelope schema for a plan-change event.
 *
 * `data` is deliberately left as an open record. We handle these events by re-fetching the plan
 * from Fullscript, so the only field we need out of the body is the treatment plan id, and the
 * per-recommendation payload's exact shape is not documented on our side. Validating the envelope
 * strictly while reading the id defensively means an unfamiliar body shape logs and stops rather
 * than throwing a parse error on a live webhook.
 */
export const FullscriptPlanUpdatedSchema = z.object({
  event: z.object({
    event: z.object({
      id: z.string(),
      type: z.enum(FULLSCRIPT_PLAN_UPDATE_EVENT_TYPES),
      created_at: z.string(),
      clinic_id: z.string(),
      data: z.record(z.string(), z.unknown()),
      integration_id: z.string().nullable(),
      vibrant: FullscriptWebhookVibrantMetadataSchema,
    }),
  }),
  // Plan id resolved once by the webhook route. The job keys its concurrency on it. The id sits
  // at one of four body paths, and a nested CEL `has()` chain for them is untestable here: an
  // invalid key fails registration for every function in the app.
  // Absent on events enqueued before the route stamped it.
  treatmentPlanId: z.string().nullish(),
});

export type FullscriptPlanUpdatedEvent = z.infer<typeof FullscriptPlanUpdatedSchema>;

/**
 * Pulls the treatment plan id out of a plan-change event body.
 *
 * Checks the shapes Fullscript is known or likely to use, in order of specificity. Returns null
 * when none match, which the caller treats as "log it and stop" — the same outcome as today,
 * minus the silence.
 */
export function resolveTreatmentPlanIdFromUpdateEvent(
  data: Record<string, unknown>,
): string | null {
  const asRecord = (value: unknown): Record<string, unknown> | null =>
    typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
  const asId = (value: unknown): string | null =>
    typeof value === "string" && value.length > 0 ? value : null;

  const treatmentPlan = asRecord(data.treatment_plan);
  const recommendation = asRecord(data.recommendation);

  return (
    asId(treatmentPlan?.id) ??
    asId(data.treatment_plan_id) ??
    asId(recommendation?.treatment_plan_id) ??
    asId(asRecord(recommendation?.treatment_plan)?.id) ??
    null
  );
}
